import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import { Loader2, Lock, User, Mail, Check, LogOut, Sun, Moon } from 'lucide-react';
import { useTheme } from 'next-themes';

interface ProfileSetupStepProps {
  user: {
    id: string;
    email?: string;
    app_metadata?: Record<string, unknown>;
    identities?: Array<{ provider: string }>;
    user_metadata?: {
      full_name?: string;
      first_name?: string;
      last_name?: string;
    };
  };
  existingProfile?: {
    full_name?: string;
    first_name?: string;
    last_name?: string;
  };
  onComplete: () => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

export function ProfileSetupStep({ 
  user, 
  existingProfile,
  onComplete, 
  isLoading, 
  setIsLoading 
}: ProfileSetupStepProps) {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const getInitialName = () => {
    const profile = existingProfile;
    const meta = user.user_metadata;
    
    if (profile?.first_name && profile?.last_name) {
      return { first: profile.first_name, last: profile.last_name };
    }
    if (meta?.first_name && meta?.last_name) {
      return { first: meta.first_name, last: meta.last_name };
    }
    if (profile?.full_name || meta?.full_name) {
      const fullName = profile?.full_name || meta?.full_name || '';
      const parts = fullName.trim().split(' ');
      return {
        first: parts[0] || '',
        last: parts.slice(1).join(' ') || ''
      };
    }
    return { first: '', last: '' };
  };

  const isGoogleUser = user.app_metadata?.provider === 'google'
    || user.identities?.some(i => i.provider === 'google') || false;

  const initialName = getInitialName();
  const [firstName, setFirstName] = useState(initialName.first);
  const [lastName, setLastName] = useState(initialName.last);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!isGoogleUser) {
      if (!password) {
        newErrors.password = 'Password is required';
      } else if (password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }
      if (password !== confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsLoading(true);
    try {
      // Update password in Supabase Auth (skip for Google users)
      if (!isGoogleUser) {
        const { error: authError } = await supabase.auth.updateUser({
          password: password,
        });

        if (authError) {
          throw authError;
        }
      }

      // Update profile with names and clear the setup flag
      const fullName = `${firstName.trim()} ${lastName.trim()}`;
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          full_name: fullName,
          needs_password_setup: false,
        })
        .eq('user_id', user.id);

      if (profileError) {
        throw profileError;
      }

      toast({
        title: 'Profile complete!',
        description: isGoogleUser
          ? 'Your account is now set up.'
          : 'Your account is now set up. You can log in anytime with your email and password.',
      });

      onComplete();
    } catch (error: any) {
      console.error('Error setting up profile:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to set up your profile. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const isValid = firstName.trim() && lastName.trim() && (isGoogleUser || (password.length >= 8 && password === confirmPassword));

  return (
    <Card className="border-primary/20 bg-card/80 backdrop-blur">
      <CardHeader className="text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <Check className="h-8 w-8 text-primary" />
        </div>
        <CardTitle className="text-2xl">Complete Your Profile</CardTitle>
        <CardDescription className="text-base">
          Welcome! Let's finish setting up your account so you can log in anytime.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Email (read-only) */}
        <div className="space-y-2">
          <Label htmlFor="email" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Email
          </Label>
          <Input
            id="email"
            type="email"
            value={user.email || ''}
            disabled
            className="bg-muted"
          />
        </div>

        {/* Name fields */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="firstName" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              First Name
            </Label>
            <Input
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Enter your first name"
              className={errors.firstName ? 'border-destructive' : ''}
            />
            {errors.firstName && (
              <p className="text-sm text-destructive">{errors.firstName}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">Last Name</Label>
            <Input
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Enter your last name"
              className={errors.lastName ? 'border-destructive' : ''}
            />
            {errors.lastName && (
              <p className="text-sm text-destructive">{errors.lastName}</p>
            )}
          </div>
        </div>

        {/* Theme preference */}
        <div className="space-y-3">
          <Label className="text-base font-medium">Appearance</Label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setTheme('dark')}
              className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                theme === 'dark'
                  ? 'border-primary bg-primary/10 text-foreground'
                  : 'border-border bg-muted/30 text-muted-foreground hover:border-primary/50'
              }`}
            >
              <Moon className="h-5 w-5" />
              <span className="font-medium">Dark</span>
            </button>
            <button
              type="button"
              onClick={() => setTheme('light')}
              className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                theme === 'light'
                  ? 'border-primary bg-primary/10 text-foreground'
                  : 'border-border bg-muted/30 text-muted-foreground hover:border-primary/50'
              }`}
            >
              <Sun className="h-5 w-5" />
              <span className="font-medium">Light</span>
            </button>
          </div>
        </div>

        {!isGoogleUser && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password" className="flex items-center gap-2">
                <Lock className="h-4 w-4" />
                Create Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                className={errors.password ? 'border-destructive' : ''}
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your password"
                className={errors.confirmPassword ? 'border-destructive' : ''}
              />
              {errors.confirmPassword && (
                <p className="text-sm text-destructive">{errors.confirmPassword}</p>
              )}
            </div>
          </div>
        )}

        <Button 
          onClick={handleSubmit} 
          className="w-full" 
          disabled={!isValid || isLoading}
          size="lg"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Setting up...
            </>
          ) : (
            'Continue to Onboarding'
          )}
        </Button>

        <div className="pt-4 border-t border-border">
          <p className="text-sm text-muted-foreground text-center mb-3">
            Not your account?
          </p>
          <Button 
            variant="ghost" 
            onClick={handleSignOut}
            className="w-full text-muted-foreground hover:text-foreground"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Sign out and return to login
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
